// src/app/auth.interceptor.ts
import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from './service/auth.service';
import { environment } from '../environments/environment';
import { from, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

/**
 * Attaches a Bearer token to outgoing HTTP requests.
 *
 * For requests to backends that have a Cognito Lambda authorizer requiring an
 * access token (timesheets, timecards, users — all share the same Cognito
 * pool, all validate via aws-jwt-verify), we attach the Cognito ACCESS token
 * (token_use: "access").
 *
 * EXCEPTION (cbhi, spec §6): when `environment.useIdTokenForTimeApis` is set,
 * the timesheets + timecards AND hc-users backends authorize the actor by the
 * `email` claim, which only the ID token carries (the crestwood access token has
 * no email claim). For those bases we attach the ID token instead. The flag is
 * absent/false on every other org, so their access-token behavior is unchanged.
 * (Confirmed for hc-users: the cbhi authorizer is tokenUse:'id' on pool
 * us-east-1_mkv1M1TEF / client 4g5ttufp13s54ppo44oi1d1rse and reads the email
 * claim; the user-activation endpoints 401 without the ID token.)
 *
 * For all other requests, we attach the ID token (legacy behavior — most other
 * services don't validate the token but it's been included historically).
 *
 * Each base URL is short-circuit guarded with `&&` so an unset / empty
 * placeholder doesn't accidentally match every request.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // NEVER attach our Cognito token to third-party hosts. They authenticate by
  // their own scheme and reject a foreign Authorization header:
  //   - Google APIs (Sheets/Drive) use ?key= and 401 if a Bearer is present.
  //   - S3 (presigned URLs) carry their own SigV4 query signature.
  // Our backends are all on *.execute-api.*.amazonaws.com (API Gateway), so the
  // token only belongs on those. This also avoids leaking our token off-domain.
  const host = (() => { try { return new URL(req.url, window.location.origin).hostname; } catch { return ''; } })();
  // API Gateway hosts look like {id}.execute-api.{region}.amazonaws.com. This
  // matches any region while excluding S3 ({bucket}.s3...amazonaws.com) and all
  // non-AWS hosts (e.g. sheets.googleapis.com).
  const isOurApiHost = host.includes('.execute-api.') && host.endsWith('.amazonaws.com');
  if (!isOurApiHost) {
    return next(req);
  }

  const timesheetsBase = (environment as any).URL_TIMESHEETS_BASE || '';
  const timecardsBase = (environment as any).URL_TIMECARDS_BASE || '';
  const usersBase = (environment as any).URL_USERS_BASE || '';
  const useIdTokenForTimeApis = !!(environment as any).useIdTokenForTimeApis;

  const isTimeApi =
    (timesheetsBase && req.url.startsWith(timesheetsBase)) ||
    (timecardsBase && req.url.startsWith(timecardsBase));
  const isUsersApi = !!(usersBase && req.url.startsWith(usersBase));
  const isAccessTokenApi = isTimeApi || isUsersApi;

  // cbhi's authorizers (time APIs AND hc-users) require the Cognito ID token
  // (token_use: "id"), matching the cbhi timesheets/timecards builds — sending
  // the access token gets rejected 401 ("Token use not allowed: access").
  // Gated on useIdTokenForTimeApis, which is set ONLY on cbhi, so sandbox/
  // crestwood keep the historical split (access token for time/users APIs).
  // Everything else (non-time, non-users) keeps the ID token as before.
  const useIdToken = (useIdTokenForTimeApis && (isTimeApi || isUsersApi)) || !isAccessTokenApi;

  // Attach the current token (re-read each time so a retry after refresh picks up
  // the new one).
  const attach = (r: HttpRequest<unknown>): HttpRequest<unknown> => {
    const token = useIdToken ? authService.getToken() : authService.getAccessToken();
    return token ? r.clone({ headers: r.headers.set('Authorization', `Bearer ${token}`) }) : r;
  };

  // PROACTIVE refresh: if the SSO (code-flow) token is within the skew window of
  // expiry, refresh before sending so the request goes out with a fresh token and
  // never 401s. No-ops for implicit / non-SSO orgs (canRefresh/isTokenExpiringSoon
  // return false), so their behavior is unchanged.
  const prep$ = (authService.isTokenExpiringSoon() && authService.canRefresh())
    ? from(authService.refreshSession())
    : of(true);

  return prep$.pipe(
    switchMap(() => next(attach(req)).pipe(
      catchError((err: HttpErrorResponse) => {
        // REACTIVE backstop — only for SSO code-flow orgs (cbhi). A 401 there means
        // the token is dead (the authorizer returns 401; business-permission denials
        // are 403, which we deliberately don't touch). Other orgs' 401s propagate
        // unchanged, so a legitimate permission 401 isn't hijacked into a re-login.
        if (err.status === 401 && authService.usesSsoCodeFlow) {
          if (authService.canRefresh()) {
            return from(authService.refreshSession()).pipe(
              switchMap(ok => {
                if (ok) return next(attach(req));        // one silent refresh + retry
                authService.handleSessionExpired();      // refresh failed → re-login
                return throwError(() => err);
              })
            );
          }
          // Code-flow org but no refresh token (expired/cleared) → hard re-login.
          authService.handleSessionExpired();
        }
        return throwError(() => err);
      })
    ))
  );
};
